'use client'

import React from 'react'
import Link from 'next/link'
import { BookOpen, Home, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'
import { useFontFamily, useFontSize } from '@/contexts/preferences-context'

export default function NotFound() {
  const { fontFamily } = useFontFamily()
  const { fontSize } = useFontSize()

  return (
    <AppErrorBoundary source="not-found-page">
      <div className="relative min-h-screen overflow-hidden">
        {/* Background Image with Overlay - Matching homepage */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url("/victorian-street-scene.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />

        {/* Dark Overlay for Readability */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25 z-10"
          style={{
            backgroundAttachment: 'fixed'
          }}
        />

        {/* Content */}
        <div className={`relative z-20 min-h-screen flex flex-col items-center justify-center px-6 font-${fontFamily} font-${fontSize}`}>
          <div className="max-w-2xl mx-auto text-center space-y-8">
            {/* 404 Heading */}
            <div className="space-y-4">
              <h1 className="text-8xl md:text-9xl font-bold text-theme-text-primary opacity-90">
                404
              </h1>
              <div className="flex items-center justify-center gap-2 text-theme-text-secondary">
                <BookOpen className="w-6 h-6 md:w-8 md:h-8" />
                <p className="text-xl md:text-2xl font-semibold">
                  This page has vanished into the mist
                </p>
              </div>
            </div>

            {/* Friendly Message */}
            <p className="text-lg md:text-xl text-theme-text-secondary max-w-xl mx-auto leading-relaxed">
              Like a character lost in the fog of Victorian London, this page seems to have wandered off into the unknown. 
              Perhaps it&apos;s time to return to familiar streets.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
              <Link href="/">
                <Button
                  size="lg"
                  className="accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)] text-lg px-8 py-6 gap-2 shadow-xl shadow-theme-lg"
                >
                  <Home className="w-5 h-5" />
                  Return Home
                </Button>
              </Link>
              <Link href="/ai-library">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 accent-border-primary/50 accent-text-primary hover:bg-theme-bg-elevated hover:border-accent-primary/50 text-lg px-8 py-6 gap-2 backdrop-blur-sm"
                >
                  <Search className="w-5 h-5" />
                  Browse Stories
                </Button>
              </Link>
            </div>

            {/* Decorative Element */}
            <div className="pt-8">
              <div className="inline-block bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-6 border border-theme-border-card shadow-theme-md">
                <p className="text-theme-text-muted text-sm">
                  Lost? Our story library awaits your discovery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppErrorBoundary>
  )
}

